/**
 * OrganiserOnboardPage — /organiser/onboard
 *
 * Single-form onboard to flag a user as an organiser. No payment,
 * no verification — you're one tap away from listing your first fayre.
 * Auto-redirects to /organiser/fayres if the user is already onboarded.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Storefront, CaretRight } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrganiserOnboardPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [displayName, setDisplayName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    // If already onboarded, skip this page
    axios.get(`${API}/organisers/me`)
      .then((r) => {
        if (r.data.organiser) {
          navigate('/organiser/fayres', { replace: true });
        } else {
          setDisplayName(user.name || '');
          setContactEmail(user.email || '');
          setChecking(false);
        }
      })
      .catch(() => setChecking(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const submit = async () => {
    if (!displayName.trim()) { toast.error('Please enter a display name'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/organisers/onboard`, {
        display_name: displayName.trim(),
        contact_email: contactEmail.trim(),
      });
      toast.success('You’re in — let’s list your first fayre 🎪');
      navigate('/organiser/fayres/new');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not onboard');
    } finally {
      setBusy(false);
    }
  };

  if (checking) return (
    <div className="min-h-screen bg-[#0F1014] text-white flex items-center justify-center" data-testid="organiser-onboard-loading">
      <p className="text-white/40 text-sm">Loading…</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0F1014] text-white" data-testid="organiser-onboard-page">
      <div className="sticky top-0 z-10 bg-[#0F1014]/95 backdrop-blur border-b border-white/5 px-5 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-white/60 hover:text-white" data-testid="onboard-back-btn">
          <ArrowLeft size={16} weight="bold" />
        </button>
        <p className="font-bold text-sm">Become an organiser</p>
      </div>
      <div className="max-w-xl mx-auto px-5 py-8">
        <div
          className="rounded-2xl p-6 mb-6"
          style={{ background: 'linear-gradient(135deg, rgba(108,85,224,0.2), rgba(194,157,82,0.12))', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <Storefront size={28} weight="fill" className="text-[#C29D52] mb-3" />
          <h1 className="text-2xl font-black leading-tight">List your wedding fayre</h1>
          <p className="text-white/60 text-sm mt-2 leading-relaxed">
            Create free public listings. Suppliers discover your event and join with one tap. Couples message suppliers before the day — your event drives bookings.
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-black uppercase tracking-wider text-white/50 mb-1.5">
              Display name
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value.slice(0, 120))}
              placeholder="e.g. Barnutopia Events"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#B89762]/40/40"
              data-testid="onboard-display-name"
            />
            <p className="text-[10px] text-white/30 mt-1">Shown on fayre pages so suppliers & couples know who's running it.</p>
          </div>
          <div>
            <label className="block text-[10px] font-black uppercase tracking-wider text-white/50 mb-1.5">
              Contact email
            </label>
            <input
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value.slice(0, 200))}
              placeholder="you@domain.com"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#B89762]/40/40"
              data-testid="onboard-contact-email"
            />
            <p className="text-[10px] text-white/30 mt-1">We keep this private — used for organiser-only notifications.</p>
          </div>
          <button
            onClick={submit}
            disabled={busy}
            className="w-full py-3 rounded-xl text-sm font-black text-white flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
            data-testid="onboard-submit-btn"
          >
            {busy ? 'Setting up…' : 'Continue'} <CaretRight size={12} weight="bold" />
          </button>
          <p className="text-[10px] text-white/30 text-center">
            Free · cancel any time · no booth sales yet (coming soon)
          </p>
        </div>
      </div>
    </div>
  );
};

export default OrganiserOnboardPage;
